'use client'

import { useState } from 'react'
import { googleFaviconUrl } from '@/lib/tools/logo'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface CuratedBrandLogoProps {
  domain: string
  logoUrl: string
  displayName: string
}

/**
 * Renders one real, hotlinked brand logo for the homepage trusted-brands
 * wall — logo only, name revealed in a popover on hover (desktop) or tap
 * (mobile, via openOnHover's press fallback — DES-34: hover-only content is
 * unreachable on touch). Falls back to the domain's favicon, then to a plain
 * text label, if the hardcoded logo URL ever breaks (third-party asset, not
 * under our control).
 */
export function CuratedBrandLogo({ domain, logoUrl, displayName }: CuratedBrandLogoProps) {
  const faviconUrl = googleFaviconUrl(domain, 128)
  const [src, setSrc] = useState(logoUrl)
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <span className="flex h-8 shrink-0 items-center text-sm font-medium text-foreground/80 sm:h-10">
        {displayName}
      </span>
    )
  }

  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        render={
          // Fixed white backing tile — several hotlinked logos render pure-dark
          // (Cluely, Johnson Law Group) and disappear against a theme-following
          // surface in dark mode. A neutral, theme-invariant plate keeps every
          // third-party logo legible in both themes.
          <button
            type="button"
            aria-label={displayName}
            className="flex h-8 shrink-0 cursor-default items-center justify-center rounded-md bg-white px-3 ring-1 ring-border/50 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 sm:h-10"
          />
        }
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- real per-brand logo assets hotlinked from each company's own site; 10 distinct external domains, not worth registering in next.config remotePatterns */}
        <img
          src={src}
          alt=""
          className="h-5 max-w-32 w-auto object-contain sm:h-6"
          onError={() => {
            if (src !== faviconUrl) {
              setSrc(faviconUrl)
            } else {
              setFailed(true)
            }
          }}
        />
      </PopoverTrigger>
      <PopoverContent side="top" className="w-auto px-2.5 py-1.5 text-xs">
        {displayName}
      </PopoverContent>
    </Popover>
  )
}
